import React, { CSSProperties } from "react";
import styled from "@emotion/styled";
import { convertFontSizeType, fontSizes, FontSizeType } from "@styles/fontStyle";
import { classNames } from "maven-lib/dist/utils/misc";
import { CrIcon } from "@maven-surface/components/base/CrIcon";
import { IconName } from "@maven-surface/components/interface/Icon";
import "./ecoa.button.scss";

interface StyledEcoaButtonFontSizeProps {
  fontSize: FontSizeType;
}
interface StyledEcoaButtonProps extends StyledEcoaButtonFontSizeProps {
  underline?: boolean;
  disabled?: boolean;
}

const StyledEcoaButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 10px 0;
  user-select: none;

  .--loading {
    .ms-Button-flexContainer {
      &:after {
        border-color: #fff;
        border-left-color: transparent;
      }
    }
  }
`;

const StyledEcoaButton = styled.button<StyledEcoaButtonProps>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 120px;
  max-width: 200px;
  padding: 10px;
  border: none;
  border-radius: 2px;
  background: #259ac5;
  color: #fff;
  box-shadow: 0 0 10px rgb(200, 200, 200);
  cursor: pointer;

  .cr-icon {
    margin: 0 7px;
    font-size: ${fontSizes["BASE"]};

    &:first-child {
      margin-left: 0;
    }
    &:last-child {
      margin-right: 0;
    }
  }

  &:focus-visible {
    outline: none;

    &:after {
      content: "";
      position: absolute;
      top: 1px;
      right: 1px;
      bottom: 1px;
      left: 1px;
      border: 2px dashed #000 !important;
      opacity: 0.5;
    }
  }

  ${({ disabled }) =>
    disabled &&
    `
        background: #ccc !important;
        color: #ddd;
        cursor: default;

        &:hover {
            background-color: #ccc;
            color: #ddd;
        }

        &:active {
            background-color: #ccc;
            color: #ddd;
        }
  `}

  ${({ underline }) =>
    underline &&
    `
        text-decoration: underline;

        &:hover {
            font-weight: 500;
        }

        &:active {
            font-weight: 550;
        }
  `}
`;

const StyledButtonText = styled.span<StyledEcoaButtonFontSizeProps>`
  font-size: ${({ fontSize }) => fontSizes[fontSize]};
`;

interface EcoaButtonProps {
  text: string | React.ReactNode;
  fontSize?: "LARGE" | "X-LARGE" | "2X-LARGE" | "3X-LARGE" | "4X-LARGE" | "5X-LARGE" | "6X-LARGE" | "7X-LARGE" | "8X-LARGE";
  useTextUnderline?: boolean;
  disabled?: boolean;
  color: "PRIMARY" | "DANGER" | "SUCCESS" | "WARNING" | "TRANSPARENT";
  iconLeft?: IconName;
  iconRight?: IconName;
  onClick?: () => void;
  style?: CSSProperties;
}

export function EcoaButton(props: EcoaButtonProps) {
  const { color, text, fontSize, iconLeft, iconRight, onClick, useTextUnderline, disabled, style } = props;

  return (
    <StyledEcoaButtonWrapper tabIndex={-1} className={classNames("ecoa-button", `--color-${String(color || "default").toLowerCase()}`)}>
      <StyledEcoaButton
        disabled={disabled}
        underline={useTextUnderline}
        fontSize={convertFontSizeType(fontSize)}
        style={style}
        onClick={() => {
          !!onClick && onClick();
        }}
      >
        {!!iconLeft && <CrIcon icon={iconLeft} />}
        <StyledButtonText fontSize={convertFontSizeType(fontSize)}>{text}</StyledButtonText>
        {!!iconRight && <CrIcon icon={iconRight} />}
      </StyledEcoaButton>
    </StyledEcoaButtonWrapper>
  );
}

EcoaButton.defaultProps = {
  color: "PRIMARY",
};
